<script setup>
import { track, EVENTS } from '@/lib/tracking'
import { resolveAttribution } from '@/Utils/attribution'

const props = defineProps({
	context: {
		type: String,
		default: 'login',
	},
	divider: {
		type: Boolean,
		default: true,
	},
})

// The core Google SSO endpoint lives on the same API base the app already uses
// for every user/auth call. It redirects the browser back to
// `{host}/app/login?a_token=...`, which SignIn.vue reads on mount.
function continueWithGoogle() {
	const base = process.env.VUE_APP_APP_API_BASE
	const host = encodeURIComponent(window.location.origin)
	// The Google OAuth round-trip only round-trips `host`, so team-invite context
	// (`join`, `continue`, and `_`-prefixed params) is dropped on the way back to
	// `/app/login?a_token=...`. Stash it here so SignIn.vue can restore it after
	// auto-login and honor the invite instead of landing on a bare dashboard.
	const search = window.location.search || ''
	if (/[?&](join|continue)=/.test(search)) {
		localStorage.setItem('pendingInviteQuery', search)
	} else {
		// No invite/continue context on this login: drop any stale value from a
		// prior, abandoned invite so it isn't re-applied on an unrelated login.
		localStorage.removeItem('pendingInviteQuery')
	}
	track(EVENTS.GOOGLE_AUTH_STARTED, { context: props.context })
	// Carry the ad click id through the OAuth round-trip so the backend can persist
	// it when it creates a NEW Google user — the `castr_attr` cookie isn't readable
	// at the API domain, so it must be passed explicitly. Resolved here (on the
	// castr.com page) where the cookie / URL / `_gcl_aw` are all still available.
	// No-op for organic logins/signups (empty value → no params appended).
	const attribution = resolveAttribution()
	const referralParam = attribution
		? `&referral=${encodeURIComponent(attribution)}&referralType=paid_ads`
		: ''
	window.location.href = `${base}/integrations/google/oauth?host=${host}${referralParam}`
}
</script>

<template>
	<div>
		<button type="button" class="google-auth-btn" @click="continueWithGoogle">
			<svg
				class="google-auth-btn__icon"
				width="18"
				height="18"
				viewBox="0 0 18 18"
				aria-hidden="true"
			>
				<path
					fill="#4285F4"
					d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
				/>
				<path
					fill="#34A853"
					d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.859-3.048.859-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z"
				/>
				<path
					fill="#FBBC05"
					d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.169.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
				/>
				<path
					fill="#EA4335"
					d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
				/>
			</svg>
			<span>Continue with Google</span>
		</button>

		<div v-if="divider" class="google-auth-divider">
			<span>or</span>
		</div>
	</div>
</template>

<style scoped>
.google-auth-btn {
	width: 100%;
	height: 44px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	border-radius: 12px;
	border: 1px solid var(--castr-border);
	background-color: var(--castr-field);
	color: var(--castr-text);
	font-size: 14px;
	font-weight: 600;
	letter-spacing: 0.01em;
	transition:
		border-color 0.15s ease,
		background-color 0.15s ease,
		transform 0.12s ease;
}
.google-auth-btn:hover {
	border-color: var(--castr-border-strong);
	background-color: rgba(255, 255, 255, 0.06);
}
.google-auth-btn:active {
	transform: translateY(1px);
}
.google-auth-btn__icon {
	flex-shrink: 0;
}
.google-auth-divider {
	display: flex;
	align-items: center;
	text-align: center;
	margin: 18px 0;
	color: var(--castr-text-muted);
	font-size: 12px;
}
.google-auth-divider::before,
.google-auth-divider::after {
	content: '';
	flex: 1;
	height: 1px;
	background-color: var(--castr-border);
}
.google-auth-divider span {
	padding: 0 12px;
}
</style>
